import { contro } from './controls'
import { activeModalStack, isGameActive, miscUiState, showModal } from './globalState'
import { options } from './optionsStorage'
import { hideNotification, notificationProxy } from './react/NotificationProvider'
import { lastConnectOptions } from './appStatus'
import { playerState } from './mineflayer/playerState'
import { pointerLock } from './utils'
import { updateMotion, initMotionTracking } from './react/uiMotion'

let lastMouseMove: number

let replayCameraYawOffset = 0
let replayCameraPitchOffset = 0

const thirdPersonDistanceMin = 1
const thirdPersonDistanceMax = 10
const thirdPersonDistanceStep = 0.5

let lastReplayBotYaw: number | undefined
let lastReplayBotPitch: number | undefined

const isPacketsReplaySession = () => !!lastConnectOptions.value?.worldStateFileContents

const normalizeAngle = (angle: number) => {
  const twoPi = Math.PI * 2
  angle = (angle + Math.PI) % twoPi
  if (angle < 0) angle += twoPi
  return angle - Math.PI
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))

const updateReplayCamera = () => {
  if (!isPacketsReplaySession()) return
  if (!bot?.entity) return
  if (playerState.reactive.perspective === 'first_person') return

  const yaw = bot.entity.yaw + replayCameraYawOffset
  const pitch = bot.entity.pitch + replayCameraPitchOffset
  appViewer.backend?.updateCamera(bot.entity.position, yaw, pitch)
}

export const resetReplayCameraOffsets = () => {
  replayCameraYawOffset = 0
  replayCameraPitchOffset = 0
  lastReplayBotYaw = undefined
  lastReplayBotPitch = undefined
}

export const getReplayCameraOffsets = () => {
  return { yaw: replayCameraYawOffset, pitch: replayCameraPitchOffset }
}

export const getReplayOrbitCameraAngles = (botYaw: number, botPitch: number) => {
  if (lastReplayBotYaw !== undefined) {
    replayCameraYawOffset += normalizeAngle(lastReplayBotYaw - botYaw)
  }
  if (lastReplayBotPitch !== undefined) {
    replayCameraPitchOffset += lastReplayBotPitch - botPitch
  }
  lastReplayBotYaw = botYaw
  lastReplayBotPitch = botPitch

  return {
    yaw: botYaw + replayCameraYawOffset,
    pitch: botPitch + replayCameraPitchOffset,
  }
}

export type CameraMoveEvent = {
  movementX: number
  movementY: number
  type: string
  stopPropagation?: () => void
}

export function onCameraMove (e: MouseEvent | CameraMoveEvent) {
  if (!isGameActive(true)) return
  if (e.type === 'mousemove' && !document.pointerLockElement) {
    if (!isPacketsReplaySession()) return
    const mouseEvent = e as MouseEvent
    if (!(mouseEvent.buttons & 1)) return
    const target = mouseEvent.target as HTMLElement | null
    if (target?.closest?.('[data-replay-ui], button, input, textarea, select, a, [role="button"], [contenteditable]')) return
    if (!target?.closest?.('#viewer-canvas') && !target?.closest?.('#ui-root')) return
  }
  e.stopPropagation?.()
  if (appViewer.playerState.utils.isSpectatingEntity()) return
  const now = performance.now()
  // todo: limit camera movement for now to avoid unexpected jumps
  if (now - lastMouseMove < 4 && !options.preciseMouseInput) return
  lastMouseMove = now
  let { mouseSensX, mouseSensY } = options
  if (mouseSensY === -1) mouseSensY = mouseSensX
  moveCameraRawHandler({
    x: e.movementX * mouseSensX * 0.0001,
    y: e.movementY * mouseSensY * 0.0001
  })
  if (bot && !isPacketsReplaySession()) {
    bot.mouse.update()
  }
  updateMotion()
}

export const moveCameraRawHandler = ({ x, y }: { x: number; y: number }) => {
  const maxPitch = 0.5 * Math.PI
  const minPitch = -0.5 * Math.PI

  appViewer.lastCamUpdate = Date.now()

  // if (viewer.world.freeFlyMode) {
  //   // Update freeFlyState directly
  //   viewer.world.freeFlyState.yaw = (viewer.world.freeFlyState.yaw - x) % (2 * Math.PI)
  //   viewer.world.freeFlyState.pitch = Math.max(minPitch, Math.min(maxPitch, viewer.world.freeFlyState.pitch - y))
  //   return
  // }

  if (!bot?.entity) return

  if (isPacketsReplaySession()) {
    const { perspective } = playerState.reactive
    if (perspective === 'first_person') {
      return
    }

    replayCameraYawOffset = normalizeAngle(replayCameraYawOffset - x)
    const currentPitch = bot.entity.pitch + replayCameraPitchOffset
    const desiredPitch = Math.max(minPitch, Math.min(maxPitch, currentPitch - y))
    replayCameraPitchOffset = desiredPitch - bot.entity.pitch

    appViewer.backend?.updateCamera(bot.entity.position, bot.entity.yaw + replayCameraYawOffset, desiredPitch)
    return
  }

  const pitch = bot.entity.pitch - y
  void bot.look(bot.entity.yaw - x, Math.max(minPitch, Math.min(maxPitch, pitch)), true)
  appViewer.backend?.updateCamera(null, bot.entity.yaw, pitch)
}

window.addEventListener('mousemove', (e: MouseEvent) => {
  onCameraMove(e)
}, { capture: true })

window.addEventListener('wheel', (e: WheelEvent) => {
  if (!isGameActive(true)) return
  if (!isPacketsReplaySession()) return
  if (playerState.reactive.perspective === 'first_person') return

  const target = e.target as HTMLElement | null
  if (target?.closest?.('[data-replay-ui], button, input, textarea, select, a, [role="button"], [contenteditable], .screen')) return

  const direction = Math.sign(e.deltaY)
  if (!direction) return

  e.preventDefault()
  const cur = playerState.reactive.thirdPersonDistance ?? 4
  playerState.reactive.thirdPersonDistance = clamp(cur + direction * thirdPersonDistanceStep, thirdPersonDistanceMin, thirdPersonDistanceMax)
  updateReplayCamera()
}, { passive: false, capture: true })

export const onControInit = () => {
  contro.on('stickMovement', ({ stick, vector }) => {
    if (!isGameActive(true)) return
    if (stick !== 'right') return
    let { x, z } = vector
    if (Math.abs(x) < 0.18) x = 0
    if (Math.abs(z) < 0.18) z = 0
    onCameraMove({
      movementX: x * 10,
      movementY: z * 10,
      type: 'stickMovement',
      stopPropagation () {}
    } as CameraMoveEvent)
    miscUiState.usingGamepadInput = true
  })
}

function pointerLockChangeCallback () {
  if (appViewer.rendererState.preventEscapeMenu) return
  if (isPacketsReplaySession()) return
  if (!pointerLock.hasPointerLock && activeModalStack.length === 0 && miscUiState.gameLoaded) {
    showModal({ reactType: 'pause-screen' })
  }
}

document.addEventListener('pointerlockchange', pointerLockChangeCallback, false)
